import { useFela } from 'react-fela';
import { FormattedMessage, useIntl } from 'react-intl';

import { RoleDropdown, type RoleDropdownProps } from '@/modules/forms';
import { IconButton, Text } from '@/modules/ui';
import { permissionByRole, permissionToRole } from '@/utilities/permission';

import type { WhitelistUser } from '../../types';
import * as felaRules from './WhitelistItem.rules';

export interface WhitelistItemProps extends WhitelistUser {
    onRemove?: (id: WhitelistUser['id']) => void;
    onUpdate?: (item: WhitelistUser) => void;
    editable?: boolean;
    hasPasskey: boolean;
    roles?: RoleDropdownProps['roles'];
}

export const WhitelistItem = ({
    id,
    name,
    email,
    accessPermissions,
    onUpdate,
    onRemove,
    editable = false,
    hasPasskey,
    roles,
}: WhitelistItemProps) => {
    const { css } = useFela();

    const { formatMessage } = useIntl();

    const accessRole = permissionToRole(accessPermissions);

    return (
        <li className={css(felaRules.container)}>
            <Text customStyle={felaRules.desktopUserItem}>{`${name} (${email})`}</Text>
            <Text variant='ParagraphSmallMedium' customStyle={felaRules.mobileUserItem}>
                {email}
            </Text>
            {editable ? (
                <div className={css(felaRules.editOptionsContainer)}>
                    <RoleDropdown
                        value={accessRole}
                        hasPasskey={hasPasskey}
                        roles={roles}
                        onChange={newRole => {
                            onUpdate?.({
                                id,
                                name,
                                email,
                                accessPermissions: permissionByRole[newRole],
                            });
                        }}
                    />
                    <IconButton
                        aria-label={formatMessage({ id: 'remove' })}
                        type='ghost'
                        icon='trash'
                        customStyle={felaRules.trashButton}
                        onClick={() => {
                            onRemove?.(id);
                        }}
                    />
                </div>
            ) : (
                <Text variant='ParagraphSmallMedium' color='foregroundsTertiary' customStyle={felaRules.roleText}>
                    <FormattedMessage id={`secret.permission.${accessRole}`} />
                </Text>
            )}
        </li>
    );
};
